import React from "react";
import AppDynamicsIcon from "src/components/ui/icons/appdynamics";
import AWSIcon from "src/components/ui/icons/aws";
import BitbucketIcon from "src/components/ui/icons/bitbucket";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import ChronosphereIcon from "src/components/ui/icons/chronosphere";
import ConfluenceIcon from "src/components/ui/icons/confluence";
import CursorIcon from "src/components/ui/icons/cursor";
import DatadogIcon from "src/components/ui/icons/datadog";
import DevinIcon from "src/components/ui/icons/devin";
import DiscordIcon from "src/components/ui/icons/discord";
import DynatraceIcon from "src/components/ui/icons/dynatrace";
import ElasticIcon from "src/components/ui/icons/elastic";
import FireHydrantIcon from "src/components/ui/icons/firehydrant";
import GithubIcon from "src/components/ui/icons/github";
import GitLabIcon from "src/components/ui/icons/gitlab";
import GoogleCloudIcon from "src/components/ui/icons/googlecloud";
import GrafanaIcon from "src/components/ui/icons/grafana";
import IncidentIoIcon from "src/components/ui/icons/incidentio";
import JiraIcon from "src/components/ui/icons/jira";
import LangfuseIcon from "src/components/ui/icons/langfuse";
import LangSmithIcon from "src/components/ui/icons/langsmith";
import LinearIcon from "src/components/ui/icons/linear";
import NewRelicIcon from "src/components/ui/icons/newrelic";
import NotionIcon from "src/components/ui/icons/notion";
import OpenSearchIcon from "src/components/ui/icons/opensearch";
import OpsgenieIcon from "src/components/ui/icons/opsgenie";
import PagerdutyIcon from "src/components/ui/icons/pagerduty";
import PGAnalyzeIcon from "src/components/ui/icons/pganalyze";
import PostgresqlIcon from "src/components/ui/icons/postgresql";
import PrometheusIcon from "src/components/ui/icons/prometheus";
import RollbarIcon from "src/components/ui/icons/rollbar";
import RootlyIcon from "src/components/ui/icons/rootly";
import SentryIcon from "src/components/ui/icons/sentry";
import SlackIcon from "src/components/ui/icons/slack";
import SnowflakeIcon from "src/components/ui/icons/snowflake";
import SocketIcon from "src/components/ui/icons/socket";
import SplunkIcon from "src/components/ui/icons/splunk";
import TemporalIcon from "src/components/ui/icons/temporal";

export interface IntegrationIconProps {
  type: string | null | undefined;
  className?: string;
}

/**
 * A component that returns the icon based on integration type
 * 
 * @param {IntegrationIconProps} props - The component props
 * @returns {JSX.Element} The integration icon
 */
const IntegrationIcon: React.FC<IntegrationIconProps> = ({ type, className = "h-10 w-10" }) => {
  if (!type) {
    return (
      <div className={`${className} flex items-center justify-center rounded-md bg-muted text-foreground font-medium`}>
        ?
      </div>
    );
  }
  
  switch (type) {
    case "AWS":
    case "CLOUDWATCH":
      return <AWSIcon className={className} />;
    case "BITBUCKET":
      return <BitbucketIcon className={className} />;
    case "CONFLUENCE":
      return <ConfluenceIcon className={className} />;
    case "DATADOG":
      return <DatadogIcon className={className} />;
    case "PROMETHEUS":
      return <PrometheusIcon className={className} />;
    case "OPENSEARCH":
      return <OpenSearchIcon className={className} />;
    case "GRAFANA_CLOUD":
      return <GrafanaIcon className={className} />;
    case "LANGSMITH":
      return <LangSmithIcon className={className} />;
    case "LANGFUSE":
      return <LangfuseIcon className={className} />;
    case "NEW_RELIC":
      return <NewRelicIcon className={className} />;
    case "CURSOR":
      return <CursorIcon className={className} />;
    case "DEVIN":
      return <DevinIcon className={className} />;
    case "BUILDKITE":
      return <BuildkiteIcon className={className} />;
    case "CHRONOSPHERE":
      return <ChronosphereIcon className={className} />;
    case "SPLUNK":
      return <SplunkIcon className={className} />;
    case "DYNATRACE":
      return <DynatraceIcon className={className} />;
    case "SENTRY":
      return <SentryIcon className={className} />;
    case "GCP_CLOUD_MONITORING":
    case "GCP":
      return <GoogleCloudIcon className={className} />;
    case "APPDYNAMICS":
      return <AppDynamicsIcon className={className} />;
    case "ELASTICSEARCH":
      return <ElasticIcon className={className} />;
    case "FIREHYDRANT":
      return <FireHydrantIcon className={className} />;
    case "SLACK":
      return <SlackIcon className={className} />;
    case "SNOWFLAKE":
      return <SnowflakeIcon className={className} />;
    case "POSTGRESQL":
      return <PostgresqlIcon className={className} />;
    case "GITHUB":
      return <GithubIcon className={className} />;
    case "GITLAB":
      return <GitLabIcon className={className} />;
    case "OPSGENIE":
      return <OpsgenieIcon className={className} />;
    case "PAGERDUTY":
      return <PagerdutyIcon className={className} />;
    case "PGANALYZE":
      return <PGAnalyzeIcon className={className} />;
    case "TEMPORAL":
      return <TemporalIcon className={className} />;
    case "NOTION":
      return <NotionIcon className={className} />;
    case "ROLLBAR":
      return <RollbarIcon className={className} />;
    case "ROOTLY":
      return <RootlyIcon className={className} />;
    case "DISCORD":
      return <DiscordIcon className={className} />;
    case "JIRA":
      return <JiraIcon className={className} />;
    case "LINEAR":
      return <LinearIcon className={className} />;
    case "INCIDENT_IO":
    case "INCIDENT.IO":
      return <IncidentIoIcon className={className} />;
    case "SOCKET":
      return <SocketIcon className={className} />;
    default:
      // Fallback icon for unknown integration types
      return (
        <div className={`${className} flex items-center justify-center rounded-md bg-muted text-foreground font-medium`}>
          {type ? type.charAt(0) : "?"}
        </div>
      );
  }
};

export default IntegrationIcon;